import React, {useState, useMemo, memo, useContext, useEffect, useCallback} from "react";
import {useTranslation} from "react-i18next";
//local
import SignContractTimeline from "./atoms/SignContractTimeline";
import {CustomTooltip} from "components/CustomTooltip";
import CustomRadioSwitch from "components/CustomRadioSwitch";
import {ConfirmModal} from "components/ConfirmModal";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {Input} from "components/Input";
import {Preloader} from "components/Preloader";
import {IconEyeCrossed, IconDownload, IconChevronDownMini} from "components/icons";
import {SignContractSigningButton} from "./atoms/SignContractSigningButton";
import {SignContractDraftButton} from "./atoms/SignContractDraftButton";
import ProjectProcurementSelect from "components/selects/ProjectProcurementSelect";
import {TagSelect} from "components/selects/TagSelect";
import {
  signContractStatusName,
  signContractStatus,
  signContractSide,
  downloadPdfButtonName,
  signContractPdfStatus,
} from "../constants";
import {getInvoiceProfiles} from "../api/contracts";
import {SignContractContext} from "../SignContractContext";
import {SignContractEditButton} from "./atoms/SignContractEditButton";
import {LockValue} from "components/LockValue";
import {hasPermission} from "helpers/helper";
import cn from "classnames";

const unnamedKey = "SIGN_CONTRACT_UNNAMED_LABEL";
const baseClass = "signing_contract__header";

const SignContractHead = memo(
  ({contract, loading, fileDataView, onChange, onDownload}) => {
    const {t} = useTranslation();
    const {user, side, status, isEditField, canSendForSignature, setEdits} =
      useContext(SignContractContext);

    const isUnnamedContract = useMemo(
      () => !contract?.name || contract?.name === unnamedKey,
      [contract?.name]
    );
    const [contractName, setContractName] = useState(null);
    const [invoiceProfiles, setInvoiceProfiles] = useState(null);
    const name = useMemo(
      () => (isUnnamedContract ? t(unnamedKey) : contract?.name),
      [contract?.name, isUnnamedContract, t]
    );
    const [isEditName, setIsEditName] = useState(false);

    const canProjectSelect = hasPermission(["ENABLE_SIGN_CONTRACT_PROJECT_ASSIGN"]);
    const disableProjectSelect = useMemo(() => contract.isHidden, [contract]);

    const [hidden, setHidden] = useState(contract?.isHidden);
    const isBuyerSide = useMemo(() => side === signContractSide.BUYER, [side]);
    const isCounterpartyColleague = useMemo(
      () => !isBuyerSide && contract?.OrganizationId === user.ActiveOrganizationId,
      [contract?.OrganizationId, user, isBuyerSide]
    );
    const showHeaderTag = useMemo(
      () => isBuyerSide || isCounterpartyColleague,
      [isBuyerSide, isCounterpartyColleague]
    );
    const contractStatus = useMemo(() => contract?.status, [contract]);
    const pdfStatus = useMemo(
      () =>
        contract?.isDocumentsReady === false
          ? signContractPdfStatus.STAMPING
          : contract?.isZipFileReady === true
          ? signContractPdfStatus.READY
          : contract?.isZipFileReady === false
          ? signContractPdfStatus.PREPARING
          : signContractPdfStatus.INITIAL,
      [contract]
    );
    const isPreparingPdfStatus = useMemo(
      () =>
        [signContractPdfStatus.PREPARING, signContractPdfStatus.STAMPING].includes(
          pdfStatus
        ),
      [pdfStatus]
    );
    const downloadPdfButtonIcon = isPreparingPdfStatus ? (
      <Preloader size="small" />
    ) : (
      <IconDownload />
    );

    const hasMembers = useMemo(
      () =>
        !!contract?.initiatingMembers?.length && !!contract?.counterpartyMembers?.length,
      [contract]
    );
    const isEnabledActionButton = useMemo(
      () => !!contract?.files?.contracts?.length && hasMembers,
      [contract, hasMembers]
    );

    const selectedProject = useMemo(
      () => (contract?.ProjectId && contract?.Project ? contract.Project : null),
      [contract?.ProjectId, contract?.Project]
    );

    const selectedProcurement = useMemo(
      () =>
        contract?.ProjectItemId && contract?.ProjectItem ? contract.ProjectItem : null,
      [contract?.ProjectItemId, contract?.ProjectItem]
    );

    const selectedInvoiceProfile = useMemo(
      () =>
        contract?.HsInvoiceProfile
          ? {
              id: contract.HsInvoiceProfile.id,
              name: contract.HsInvoiceProfile.name,
            }
          : null,
      [contract?.HsInvoiceProfile]
    );

    const handleProjectChange = (project) => {
      onChange(null, {ProjectId: project?.id ?? null, ProjectItemId: null}, "update");
    };

    const handleProcurementChange = (procurement) =>
      onChange(null, {ProjectItemId: procurement?.id ?? null}, "update");

    const clickChangeName = (name) => {
      const value = name.trim() ? name : "SIGN_CONTRACT_UNNAMED_LABEL";
      return onChange("name", value, "refresh").finally(() => setIsEditName(false));
    };

    const clickChangeHiddenStatus = (checked) =>
      checked
        ? changeHiddenStatus(checked)
        : ConfirmModal({
            title: t("SIGN_CONTRACT_HIDE_MODAL_TITLE"),
            text: t("SIGN_CONTRACT_HIDE_MODAL_TEXT"),
            type: "warning",
            onOk: () => changeHiddenStatus(checked),
          });

    const changeHiddenStatus = (checked) => {
      setHidden(checked);
      onChange("isHidden", checked, "refresh").catch(() => setHidden(!checked));
    };

    const handleInvoiceProfileChange = (profile) =>
      onChange("HsInvoiceProfileId", profile?.id ?? null, "refresh");

    const getProfiles = useCallback(
      async () =>
        await getInvoiceProfiles({size: 9999}).then(({invoiceProfiles}) =>
          setInvoiceProfiles(invoiceProfiles.map(({id, name}) => ({id, name})))
        ),
      []
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => setEdits({name: isEditName}), [isEditName]);

    useEffect(() => {
      const refreshContractData =
        isPreparingPdfStatus && status.isSigned
          ? setInterval(() => onChange(undefined, undefined, "refresh"), 3000)
          : null;
      return () => refreshContractData && clearInterval(refreshContractData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPreparingPdfStatus, status.isSigned]);

    return (
      <React.Fragment>
        <div className={baseClass}>
          <div className={`${baseClass}__title-statuses`}>
            <div className="df-row-center fg2">
              {isEditName ? (
                <Input
                  small
                  boldText
                  flatView
                  fullWidth
                  autoFocus={true}
                  customHeight="32"
                  fontSize="20"
                  placeholder={t("SIGN_CONTRACT_NAME_INPUT_PLACEHOLDER")}
                  defaultValue={name}
                  value={contractName}
                  onChange={(e) => {
                    e.stopPropagation();
                    setContractName(e.target.value);
                  }}
                  onBlur={(e) => clickChangeName(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && clickChangeName(e.target.value)}
                />
              ) : (
                <h3 className={cn(`${baseClass}__title`, {gray: isUnnamedContract})}>
                  {name}
                </h3>
              )}
              {isBuyerSide && status.isDraft && (
                <SignContractEditButton
                  edit={isEditName}
                  showName
                  onEdit={() => setIsEditName(true)}
                  onSubmit={() => clickChangeName(contractName)}
                />
              )}
              {showHeaderTag && (
                <LockValue
                  canPermissions={["ENABLE_SIGN_CONTRACT_PROJECT_ASSIGN"]}
                  additionalShowCondition={isCounterpartyColleague}
                  needLabel
                  needShowModalToSubscribe
                  addClassIcon="ml-0"
                >
                  <ProjectProcurementSelect
                    selectedProject={selectedProject}
                    selectedProcurement={selectedProcurement}
                    onProjectChange={handleProjectChange}
                    onProcurementChange={handleProcurementChange}
                    disabled={disableProjectSelect}
                    readOnly={!canProjectSelect}
                    addClass="ml-4"
                  />
                </LockValue>
              )}
              {showHeaderTag && !fileDataView && (
                <TagSelect
                  list={invoiceProfiles}
                  value={selectedInvoiceProfile}
                  text={{
                    emptySelect: "SIGN_CONTRACT_INVOICE_PROFILE_CHOOSER_SELECT",
                    emptyData: "SIGN_CONTRACT_INVOICE_PROFILE_CHOOSER_NO_DATA",
                    emptySearchInput: "SIGN_CONTRACT_INVOICE_PROFILE_CHOOSER_SEARCH",
                    info: "SIGN_CONTRACT_INVOICE_PROFILE_CHOOSER_INFO",
                  }}
                  tagColor="black"
                  borderTagColor="black"
                  suffixIcon={{
                    empty: <IconChevronDownMini addClass="mr-0" />,
                  }}
                  canClear
                  onMenuToggle={(open) => open && !invoiceProfiles && getProfiles()}
                  onChange={handleInvoiceProfileChange}
                  addTagClass={`df-row-center fz-10 brr-11 ${
                    selectedInvoiceProfile ? "pr-[3]" : "pr-1"
                  }`}
                  addClass="ml-4"
                />
              )}
            </div>
            <div className={`${baseClass}__statuses`}>
              {isBuyerSide && (
                <CustomRadioSwitch
                  checked={hidden}
                  uppercase
                  small
                  addWrapperClass="mr-1 ml-2"
                  isActive={clickChangeHiddenStatus}
                />
              )}
              {isBuyerSide && (
                <CustomTooltip text="SIGN_CONTRACT_HIDDEN_TOGGLE_TOOLTIP">
                  <div className="mt-1 mr-4">
                    <IconEyeCrossed />
                  </div>
                </CustomTooltip>
              )}
              {isBuyerSide && status.isSigning && (
                <SignContractDraftButton
                  loading={loading}
                  onClick={() => onChange("status", signContractStatus.DRAFT)}
                />
              )}
              {!!contract.number && <span>{`# ${contract.number}`}</span>}
              {isBuyerSide && (
                <span
                  className={cn("__uppercase __bold ml-4", contractStatus?.toLowerCase())}
                >
                  {t(signContractStatusName[contractStatus])}
                </span>
              )}
              {status.isSigned && (
                <ButtonTransparent
                  name={t(downloadPdfButtonName[pdfStatus])}
                  small
                  icon={downloadPdfButtonIcon}
                  loading={loading}
                  disabled={isPreparingPdfStatus}
                  onClick={onDownload}
                  addClass={`ml-4${isPreparingPdfStatus ? " pl-0" : ""}`}
                />
              )}
            </div>
          </div>
          {status.isDraft && (
            <div className={`${baseClass}__timeline-actions`}>
              <SignContractTimeline contract={contract} addClass="mla" />

              <LockValue
                canPermissions={["CHANGE_STATUS_SIGN_CONTRACT_DRAFT_TO_SIGNING"]}
                additionalShowCondition={canSendForSignature}
                needLabel
                needLeftIcon
                addClassIcon="ml-4"
              >
                <SignContractSigningButton
                  loading={loading}
                  disabled={!isEnabledActionButton || isEditField}
                  disabledTooltip={
                    isEditField
                      ? "SIGN_CONTRACT_ACTION_BUTTON_TO_SIGNING_EDIT_DISABLED"
                      : undefined
                  }
                  onClick={
                    canSendForSignature
                      ? () => onChange("status", signContractStatus.SIGNING)
                      : undefined
                  }
                  addClass={canSendForSignature ? "ml-4" : "ml-1"}
                />
              </LockValue>
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }
);

export default SignContractHead;
